import {flushSync} from 'react-dom';
import { ChakraProvider } from "@chakra-ui/react";
import { FormikErrors, FormikProps } from "formik";
import { DateTime } from "luxon";
import React, { useContext, useState } from "react";
import { ReactSpreadsheetImport } from "react-spreadsheet-import";
import { SelectOption } from "react-spreadsheet-import/types/types";
import AppContext from "../../../AppContext";
import { countries } from "../../../data/countries";
import { provinces } from "../../../data/province";
import { states } from "../../../data/states";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link as MUILink, Typography } from '@mui/material';
import { spreadsheetImportFields } from "./spreadsheet-fields";
import NextLink from 'next/link';
import { updatePeopleTab } from "./QuoteHelpers";
import { ArrowCircleRight, FileDownload } from '@mui/icons-material';
import Image from 'next/image';
import { PurchaseDetails, PrimaryInsured } from '../../purchase/PurchaseData.interface';

interface Props {
  formikProps: FormikProps<PurchaseDetails>,
  setPeopleTabText: React.Dispatch<React.SetStateAction<{peopleText: string, peopleValid: boolean}>>,
}

function lookupLabel(value:string, dataset:SelectOption[]):string | undefined {
  return dataset.find((dataOption) => value === dataOption.value)?.label
}

const costRegex = /^([0-9]+\.[0-9]{2}|[0-9]+)$/;

const SpreadsheetImportModal = ({formikProps, setPeopleTabText}:Props): JSX.Element => {
  const appState: any = useContext(AppContext);
  const [formSubmitting, setFormSubmitting] = useState(false)
  
  const fields = spreadsheetImportFields;

  const handleCloseLoadingModal = () => {
    setFormSubmitting(false);
  }

  const handleOpenImportModal = () => {
    appState.setAppState({
      ...appState.state,
      importModalOpen: true,
      importInfoModalOpen: false,
      quoteModalOpen: false
    })
  };
  
  const handleCloseInfoModal = () => {
    appState.setAppState({
      ...appState.state,
      importModalOpen: false,
      importInfoModalOpen: false,
      quoteModalOpen: true
    })
  };
  
  const handleCloseImportModal = () => {
    appState.setAppState({
      ...appState.state,
      importInfoModalOpen: false,
      importModalOpen: false,
      quoteModalOpen: true
    });
  }

  const tryParseDate = (dateStr: string, dob = false): DateTime | null => {
  
    const formatPatterns: { regex: RegExp; format: string }[] = [
      {
        regex: new RegExp("^(0?[1-9]|1[0-2])\\/(0?[1-9]|[12]\\d|3[01])\\/(\\d{4})$"),
        format: "M/d/yyyy"
      },
      {
        regex: new RegExp("^(0?[1-9]|1[0-2])\\/(0?[1-9]|[12]\\d|3[01])\\/(\\d{2})$"),
        format: "M/d/yy"
      }
    
    ];
  
    // Check each pattern
    for (const { regex, format } of formatPatterns) {
      const match = dateStr.match(regex);
      if (match) {
        let parsedDate = DateTime.fromFormat(dateStr, format);
  
        // Handle M/D/YY format and adjust century if needed
        if (format === "M/d/yy") {
          const currentYear = DateTime.now().year;
          const currentCentury = Math.floor(currentYear / 100) * 100;
          const twoDigitYear = parseInt(match[3], 10);
          let fullYear = currentCentury + twoDigitYear;
  
          // Adjust for DOB: prevent future birthdates
          if (dob && fullYear > currentYear) {
            fullYear -= 100; // Adjust to previous century if future year
          }
  
          parsedDate = DateTime.fromObject({
            year: fullYear,
            month: parsedDate.month,
            day: parsedDate.day,
          });
        }
  
        // If DOB is true, check for future dates
        if (dob && parsedDate > DateTime.now()) {
          return null;
        }
  
        return parsedDate;
      }
    }
  
    return null;  // If no matching format found
  };
  
  const onSubmit = async (importedValues:{all:any,validData:any[],invalidData:any[]}) => {
    setFormSubmitting(true);

    const reformattedValues = importedValues.validData.map((value:any):Partial<PrimaryInsured> => {
      const now = DateTime.now();

      const dob = tryParseDate(value['Date of Birth'], true) ?? now;
      
      const age = DateTime.now().diff(dob, 'years');

      const isMale = /^(m|male)$/i.test(value['Gender']);

      return {
        firstName: value['First Name'],
        lastName: value['Last Name'],
        email: value['Email'],
        startDate:tryParseDate(value['Start Date'])?.toJSDate(),
        endDate: tryParseDate(value['End Date'])?.toJSDate(),
        dateOfBirth: dob.toJSDate(),
        age: Math.floor(age.years),
        tripCost: value['Trip Cost'] ? value['Trip Cost'] : 0,
        hasSpouse: false,
        hasChildren: false,
        children: [],
        genderObject: {
          value: isMale ? 'male' : 'female',
          label: isMale ? 'Male' : 'Female',
        },
        citizenshipObject: {
          value: value['Country of Citizenship'],
          label: lookupLabel(value['Country of Citizenship'], countries.concat({ label: 'United States', value: 'USA' })) as string
        },
        residencyCountryObject: {
          value: value['Country of Residence'],
          label: lookupLabel(value['Country of Residence'], countries.concat({ label: 'United States', value: 'USA' })) as string
        },
        residencyStateObject: {
          value: value['State / Province'],
          label: lookupLabel(value['State / Province'], states.concat(provinces)) as string
        }
      }
    })

    // organize into primary/spouse/child

    const finalArray = [reformattedValues[0]];
    let primaryIndex = 0;

    for (let i = 1; i < reformattedValues.length; i++) {
      if (importedValues.validData[i].Type === 'Spouse') {
        if (!finalArray[primaryIndex].hasSpouse) {
          delete reformattedValues[i].hasSpouse;
          delete reformattedValues[i].hasChildren;
          delete reformattedValues[i].children;
          delete reformattedValues[i].startDate;
          delete reformattedValues[i].endDate;
          
          finalArray[primaryIndex].hasSpouse = true;
          finalArray[primaryIndex].spouse = reformattedValues[i];
        } else {
          finalArray.push(reformattedValues[i]);
          primaryIndex++;  
        }
      } else if (importedValues.validData[i].Type === 'Child') {
        delete reformattedValues[i].hasSpouse;
        delete reformattedValues[i].hasChildren;
        delete reformattedValues[i].children;
        delete reformattedValues[i].startDate;
        delete reformattedValues[i].endDate;
        
        finalArray[primaryIndex].hasChildren = true;
        finalArray[primaryIndex].children?.push(reformattedValues[i])
      } else if (importedValues.validData[i].Type === 'Primary') {
        finalArray.push(reformattedValues[i]);
        primaryIndex++;
      }
    }
    
    await flushSync(() => formikProps.setFieldValue('primaryInsured', finalArray, true)) 
    
    await setTimeout(()=>{
      setPeopleTabText({
        peopleText: updatePeopleTab(formikProps.values.primaryInsured, formikProps.errors.primaryInsured as FormikErrors<Partial<PrimaryInsured>>[]),
        peopleValid: formikProps.errors.primaryInsured === undefined,
      });
      
      
      setFormSubmitting(false);
    }, 5000)
  }

  const patchCountry = (value: string | undefined) => {
    if (!value) {
      return ''
    };

    const normalizedValue = value.toLowerCase();

    const usPattern = /(us|usa)/i;
    if (usPattern.test(normalizedValue)) {
      return 'USA';
    }
    const countriesWithUSA  = [{ "value": "USA", "label": "United States" }, ...countries]
    const countryMatch = countriesWithUSA.find(c => c.label.toLowerCase() === normalizedValue || c.value.toLowerCase() === normalizedValue);
    return countryMatch?.value || value;
  }

  const patchState = (value: string | undefined) => {
    if (!value) {
      return ''
    }
    const normalizedValue = value.toLowerCase();
    const stateMatch = [...states, ...provinces].find(s => s.label.toLowerCase() === normalizedValue || s.value.toLowerCase() === normalizedValue);
    return stateMatch?.value || value;
  }

  return (
    <>
      <ChakraProvider>
        <ReactSpreadsheetImport
          isOpen={appState.state.importModalOpen}
          onClose={handleCloseImportModal}
          onSubmit={onSubmit}
          fields={fields}
          allowInvalidSubmit={false}
          rowHook={(data, addError) => {
            // Validation
            data['Country of Citizenship'] = patchCountry(data['Country of Citizenship'] as string);
            data['Country of Residence'] = patchCountry(data['Country of Residence'] as string);
            data['State / Province'] = patchState(data['State / Province'] as string);

            const dob = tryParseDate(data['Date of Birth'] as string, true);
            if (dob) {
              data['Date of Birth'] = dob.toFormat('MM/dd/yyyy');
            } else {
              addError("Date of birth", { message: "Invalid date format. Use MM/DD/YY or MM/DD/YYYY.", level: "error" });
            }
            const startDate = data['Start Date'] as string;
            
            if (data.Type === "Primary" && (!startDate || !tryParseDate(startDate))) {
              addError("Start Date", { message: "Date must be formatted as m/d/yy or mm/dd/yy.", level: "error" })
            }
            
            const tripCost = data['Trip Cost'] as string;

            if (formikProps.values.useOverallTripCost === 'no' && (!tripCost || tripCost === '')) {
              addError("Trip Cost", { message: "Trip Cost is required.", level: "error" })
            } else if (formikProps.values.useOverallTripCost === 'no' && !tripCost.match(costRegex)) {
              addError("Trip Cost", { message: "Trip Cost must be a whole number or decimal value (e.g., 1300.43).", level: "error" })
            } else if (formikProps.values.useOverallTripCost === 'no' && parseFloat(tripCost) < 1000) {
              addError("Trip Cost", { message: "Minimum trip cost for trip cancellation or trip interruption coverage is $1,000.", level: "error" })
            }
            
            if (data.Type !== "Primary") {
              return { ...data, 'Start Date': '', 'End Date': '' };
            } else {
              return { ...data }
            }
          }}
        />
      </ChakraProvider>

      <Dialog
        open={appState.state.importInfoModalOpen}
        onClose={handleCloseInfoModal}
        scroll={'body'}
      >
        <DialogTitle>
          Spreadsheet Import Tool
        </DialogTitle>
        <DialogContent>
          <NextLink href={`/api/import-template`} passHref>
            <MUILink underline="none" component="a" target={'_blank'} sx={{fontSize: '18px', mb: 1, display: 'flex', alignItems: 'center'}}>
              <FileDownload color="success" sx={{ fontSize: '36px', mr: 1}}/>
              Click here to download the spreadsheet import template.&nbsp;
            </MUILink>
          </NextLink>
          
          <Box sx={{ paddingX: 4, mb: 3 }}>
            <ul>
              <li>Every spreadsheet MUST be imported in this format.</li>
              <li>We suggest listing every traveler as a primary traveler.</li>
              <li>All fields MUST be filled out to be imported correctly.</li>
            </ul>
          </Box>

          <Box sx={{ fontSize: '18px', display: 'flex', alignItems: 'center', mb: 1 }}>
            <ArrowCircleRight color="success" sx={{ fontSize: '36px', mr: 1 }} />
            Continue through the FOUR validation steps by clicking NEXT.
          </Box>

          <Box sx={{ paddingX: 2 }}>
            <Image src="/assets/shared/four-steps.png" alt="Four Import Steps" width={1610} height={150} />
          </Box>

          <Box sx={{ paddingX: 4 }}>
            <ol>
              <li>Upload File &amp; Select the sheet &quot; Team Roster&quot; &mdash; Click Next</li>
              <li>The header row will be selected in purple &mdash; Click Next</li>
              <li>If you use the template above you will not need to match any columns &mdash; Click Next</li>
              <li>If you have issues they will be highlighted <Typography component={'span'} sx={{color: 'red'}}>RED</Typography> on this step. You can correct the issues in this step. &mdash; Click CONFIRM</li>
            </ol>
          </Box>
          
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseInfoModal}
          >
            Cancel
          </Button>
          <Button
            onClick={handleOpenImportModal}
            variant={'contained'}
          >
            Begin Import
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog
        open={formSubmitting}
        onClose={handleCloseLoadingModal}
        scroll={'body'}
      >
        <DialogTitle>
          Submitting...
        </DialogTitle>
        <DialogContent>
          Submitting...
        </DialogContent>
      </Dialog>
    </>
  )
}

export default SpreadsheetImportModal;
